import { useState } from 'react';
import Reveal from '@/components/base/Reveal';
import SectionHeading from '@/components/feature/SectionHeading';
import { teamMembers } from '@/mocks/aboutData';

interface TeamMember {
  id: string;
  name: string;
  role: string;
  image: string;
  note: string;
}

interface TeamPhotoProps {
  file: string;
  name: string;
  role: string;
  shape: 'portrait' | 'circle';
}

function TeamPhoto({ file, name, role, shape }: TeamPhotoProps) {
  const [failed, setFailed] = useState(false);
  const base = import.meta.env.BASE_URL || '/';
  const src = file.startsWith('http') ? file : `${base}${file.replace(/^\//, '')}`;

  if (failed) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-secondary-50 text-secondary-500">
        <i className={`ri-user-line ${shape === 'circle' ? 'text-2xl' : 'text-4xl'}`}></i>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={`${name}, ${role} at Best Research Pharma`}
      title={`${name} - ${role}, Best Research Pharma`}
      onError={() => setFailed(true)}
      className={
        shape === 'circle'
          ? 'w-full h-full object-cover object-top'
          : 'w-full h-full object-cover object-top transition-transform duration-700 hover:scale-105'
      }
    />
  );
}

function LeaderCard({ member, delay }: { member: TeamMember; delay: number }) {
  return (
    <Reveal delay={delay}>
      <article className="h-full rounded-lg border border-background-200 bg-background-50 overflow-hidden">
        <div className="w-full h-80 md:h-[26rem] bg-background-100 overflow-hidden">
          <TeamPhoto file={member.image} name={member.name} role={member.role} shape="portrait" />
        </div>
        <div className="p-6 md:p-7 text-center">
          <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-950">{member.name}</h3>
          <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-primary-600">{member.role}</p>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">{member.note}</p>
        </div>
      </article>
    </Reveal>
  );
}

function StaffCard({ member, delay }: { member: TeamMember; delay: number }) {
  return (
    <Reveal delay={delay}>
      <article className="h-full rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
        <div className="mx-auto w-28 h-28 md:w-32 md:h-32 rounded-full overflow-hidden border-2 border-secondary-100 bg-background-100">
          <TeamPhoto file={member.image} name={member.name} role={member.role} shape="circle" />
        </div>
        <div className="mt-4 text-left">
          <h3 className="font-heading text-base font-semibold text-foreground-950">{member.name}</h3>
          <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary-600">{member.role}</p>
          <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">{member.note}</p>
        </div>
      </article>
    </Reveal>
  );
}

export default function AboutTeam() {
  const leaders = teamMembers.slice(0, 2);
  const members = teamMembers.slice(2);

  return (
    <section className="w-full bg-background-100 py-14 md:py-20">
      <div className="w-full px-4 md:px-8">
        <SectionHeading
          eyebrow="Our People"
          title="The Team Behind Every Formulation"
          description="A blend of experienced professionals and passionate researchers, all working together for one purpose — better herbal care."
        />

        <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
          {leaders.map((member, index) => (
            <LeaderCard key={member.id} member={member} delay={index * 90} />
          ))}
        </div>

        {members.length > 0 && (
          <div className="mt-12 md:mt-16">
            <SectionHeading eyebrow="Our Core Team" title="The People Who Keep Us Moving" />
            <div className="mt-8 md:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {members.map((member, index) => (
                <StaffCard key={member.id} member={member} delay={index * 90} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}